import React, { useEffect, useState, useContext } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import { PreferencesContext } from "../context/PreferencesContext";
import { AuthContext } from "../context/AuthContext";
import { VENDOR_TYPES, vendorTypeLabel } from "../utils/vendorConstants";

const VendorFormDialog = ({
  open,
  onClose,
  onSave,
  vendor,
  orgs,
  years,
  user,
}) => {
  const [form, setForm] = useState({
    organization: "",
    academicYear: "",
    name: "",
    code: "",
    vendorType: "book",
    contact: "",
    gstin: "",
  });

  const { showMessage } = useMessage();

  useEffect(() => {
    setForm(
      vendor || {
        organization: "",
        academicYear: "",
        name: "",
        code: "",
        vendorType: "book",
        contact: "",
        gstin: "",
      }
    );
  }, [vendor, open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    if (!form.organization) {
      showMessage("Organization is required", "warning");
      return false;
    }
    if (!form.academicYear) {
      showMessage("Academic Year is required", "warning");
      return false;
    }
    if (!form.name.trim()) {
      showMessage("Name is required", "warning");
      return false;
    }
    if (!form.code.trim()) {
      showMessage("Code is required", "warning");
      return false;
    }
    if (!form.vendorType) {
      showMessage("Vendor type is required", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (user.role !== "super_admin") {
      form.organization = user.organization;
    }

    if (validate()) onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{vendor ? "Edit" : "Add"} Vendor</DialogTitle>
      <DialogContent>
        {user.role == "super_admin" && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Organization</InputLabel>
            <Select
              name="organization"
              value={form.organization}
              onChange={handleChange}
            >
              {orgs.map((org) => (
                <MenuItem key={org._id} value={org._id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth margin="normal">
          <InputLabel>Academic Year</InputLabel>
          <Select
            name="academicYear"
            value={form.academicYear}
            onChange={handleChange}
          >
            {years.map((ay) => (
              <MenuItem key={ay._id} value={ay._id}>
                {ay.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Code"
          name="code"
          value={form.code}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Supplier type</InputLabel>
          <Select
            name="vendorType"
            value={form.vendorType || "book"}
            label="Supplier type"
            onChange={handleChange}
          >
            {VENDOR_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Contact"
          name="contact"
          value={form.contact}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="GSTIN"
          name="gstin"
          value={form.gstin}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {vendor ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Vendors = () => {
  const { organization: prefOrg, academicYear: prefYear } =
    useContext(PreferencesContext);
  const { user } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [vendors, setVendors] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [years, setYears] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchOrgs = async () => {
    try {
      const res = await axios.get("/orgs");
      setOrgs(res.data);
    } catch {
      showMessage("Failed to load organizations", "error");
    }
  };

  const fetchYears = async () => {
    try {
      const res = await axios.get("/academic-years");
      setYears(res.data);
    } catch {
      showMessage("Failed to load academic years", "error");
    }
  };

  const fetchVendors = async () => {
    try {
      const params = {
        orgId: user.role === "super_admin" ? prefOrg : user.organization,
        academicYear: prefYear,
      };
      const res = await axios.get("/vendors", { params });
      setVendors(res.data);
    } catch {
      showMessage("Failed to load vendors", "error");
    }
  };

  useEffect(() => {
    if (user.role === "super_admin") fetchOrgs();
    fetchYears();
    fetchVendors();
  }, []);

  const handleSave = async (form) => {
    try {
      if (editingVendor) {
        await axios.put(`/vendors/${editingVendor._id}`, form);
        showMessage("Vendor updated", "success");
      } else {
        await axios.post("/vendors", form);
        showMessage("Vendor added", "success");
      }
      setOpen(false);
      fetchVendors();
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to save vendor", "error");
    }
  };

  const filteredVendors = vendors.filter((v) => {
    if (typeFilter === "all") return true;
    return (v.vendorType || "book") === typeFilter;
  });

  const handleSoftDelete = async (vendor) => {
    const confirmed = await confirm({
      title: vendor.isActive ? "Deactivate Vendor" : "Activate Vendor",
      message: `Are you sure you want to ${
        vendor.isActive ? "deactivate" : "activate"
      } this vendor?`,
    });
    if (confirmed) {
      try {
        await axios.delete(`/vendors/${vendor._id}`, {
          data: { isActive: !vendor.isActive },
        });
        showMessage(
          `Vendor ${vendor.isActive ? "deactivated" : "activated"}`,
          "success"
        );
        fetchVendors();
      } catch {
        showMessage("Action failed", "error");
      }
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Vendors
      </Typography>
      <Button
        variant="contained"
        sx={{ mb: 2, mr: 2 }}
        onClick={() => {
          setEditingVendor(null);
          setOpen(true);
        }}
      >
        Add Vendor
      </Button>

      <FormControl sx={{ mb: 2, minWidth: 180 }}>
        <InputLabel>Filter by type</InputLabel>
        <Select
          value={typeFilter}
          label="Filter by type"
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <MenuItem value="all">All suppliers</MenuItem>
          {VENDOR_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <CommonTable
        columns={[
          {
            label: "Status",
            renderCell: (v) => (
              <Chip
                label={v.isActive ? "Active" : "Deactivated"}
                color={v.isActive ? "success" : "error"}
                size="small"
              />
            ),
          },
          {
            label: "Type",
            renderCell: (v) => vendorTypeLabel(v.vendorType || "book"),
          },
          { label: "Name", field: "name" },
          { label: "Code", field: "code" },
          { label: "Contact", field: "contact" },
          { label: "GSTIN", field: "gstin" },
          {
            label: "Actions",
            sortable: false,
            renderCell: (v) => (
              <>
                {(user.role === "super_admin" ||
                  (user.role === "admin" &&
                    v.organization === user.organization)) && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingVendor(v);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color={v.isActive ? "error" : "success"}
                      onClick={() => handleSoftDelete(v)}
                    >
                      {v.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  </>
                )}
              </>
            ),
          },
        ]}
        rows={filteredVendors}
      />

      <VendorFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        vendor={editingVendor}
        orgs={orgs}
        years={years}
        user={user}
      />
    </Box>
  );
};

export default Vendors;
